import moment from "moment";
import {
  EMPTY_CELL_VALUE,
  toOptionalIntegerId,
  toOptionalIntegerIds,
} from "./overdueUtils";
import { buildDataOpsExportFileName, downloadDataOpsExcel } from "./utils";

/** Permission for Data Ops → RCA (Analytics + RCA tracking hub). */
export const DATA_OPS_RCA_VIEW_PERMISSION = "view_data_ops_dashboard_rca_info";

export const canAccessDataOpsRca = (permissionMap = {}) => (
  Boolean(permissionMap[DATA_OPS_RCA_VIEW_PERMISSION])
);

/** RCA status codes (filters + API `status` param). */
export const RCA_STATUS = {
  ACTIVE: 1,
  COMPLETED: 2,
  IN_PROGRESS: 3,
  ON_HOLD: 4,
  NOT_STARTED: 5,
};

/** Data Ops workflow status tokens (row detail / patch). */
export const RCA_WORKFLOW_STATUS = {
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  ON_HOLD: "on_hold",
  COMPLETED: "completed",
};

export const RCA_STATUS_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: String(RCA_STATUS.ACTIVE), label: "Active" },
  { value: String(RCA_STATUS.COMPLETED), label: "Completed" },
  { value: String(RCA_STATUS.IN_PROGRESS), label: "In Progress" },
  { value: String(RCA_STATUS.ON_HOLD), label: "On-Hold" },
  { value: String(RCA_STATUS.NOT_STARTED), label: "Not Started" },
];

/** Numeric status options for RCA details form (same codes as filters / API `status`). */
export const RCA_DETAIL_STATUS_OPTIONS = [
  { value: RCA_STATUS.ACTIVE, label: "Active" },
  { value: RCA_STATUS.COMPLETED, label: "Completed" },
  { value: RCA_STATUS.IN_PROGRESS, label: "In Progress" },
  { value: RCA_STATUS.ON_HOLD, label: "On-Hold" },
  { value: RCA_STATUS.NOT_STARTED, label: "Not Started" },
];

/**
 * Shape a tracking/history RCA row for AddRcaSubmitModal (edit flow).
 * @param {object} record
 * @param {object} [context]
 * @returns {object}
 */
export const buildRcaSubmitRowFromTrackingRecord = (record = {}, context = {}) => ({
  ...context,
  ...record,
  rca_id: record.rca_id ?? record.id,
  rca_pk: record.rca_pk ?? record.rca_id ?? record.id,
  tests_in_sample_pk: record.tis_pk ?? context.tis_pk,
  tis_pk: record.tis_pk ?? context.tis_pk,
  wo_id_pk: record.wo_id_pk
    ?? record.workorder_id_pk
    ?? record.workorder_pk
    ?? record.workorder_id
    ?? context.wo_id_pk,
  workorder_id: context.workorder_id ?? record.workorder_id,
  genista_id: context.genista_id ?? record.genista_id,
  test_name: record.test_name ?? record.analysis ?? context.test_name ?? context.analysis,
  analysis: record.analysis ?? record.test_name ?? context.analysis ?? context.test_name,
  test_category: record.category_name
    ?? record.test_category
    ?? context.test_category
    ?? context.category_name
    ?? context.test_category_name,
  customer_name: record.customer_company
    ?? record.customer_name
    ?? context.customer_company
    ?? context.customer_name
    ?? context.customer_id
    ?? "",
  sample_id: record.sample_id ?? context.sample_id ?? "",
  root_causes: record.rca_description ?? record.root_causes ?? record.root_cause ?? "",
  conclusions: record.conclusion ?? record.conclusions ?? "",
  on_hold_reason: record.onhold_reason ?? record.on_hold_reason ?? "",
  expected_complete_date: record.expected_complete_date,
  assigned_to: record.assigned_to,
  s3_keys: record.s3_keys,
  rca_status: record.rca_status,
});

/**
 * POST / PATCH rca_tracking body (Data Ops RCA details modal).
 * @param {object} params
 * @param {boolean} params.isEdit
 */
export const buildRcaTrackingSavePayload = ({
  isEdit,
  rcaPk,
  tisPk,
  userId,
  rcaStatus,
  assignedTo,
  expectedCompleteDate,
  s3Keys,
  rootCauses,
  conclusions,
  onHoldReason,
}) => {
  const payload = {
    user_id: userId,
    rca_status: rcaStatus,
    assigned_to: assignedTo,
    expected_complete_date: expectedCompleteDate,
    conclusion: conclusions.trim(),
    rca_description: rootCauses.trim(),
  };

  if (isEdit) {
    payload.s3_keys = Array.isArray(s3Keys) ? s3Keys : [];
  } else if (Array.isArray(s3Keys) && s3Keys.length > 0) {
    payload.s3_keys = s3Keys;
  }

  if (rcaStatus === RCA_STATUS.ON_HOLD) {
    payload.onhold_reason = onHoldReason.trim();
  }

  if (isEdit) {
    payload.rca_pk = rcaPk;
  } else {
    payload.tis_pk = tisPk;
  }

  return payload;
};

/** Tooltip on hover when RCA details Submit button is disabled (Data Ops RCA RCA add/edit action flow). */
export const getRcaSubmitDisabledReason = ({
  selectedRow,
  status,
  expectedCompleteDate,
  rootCauses,
  conclusions,
  assignedTo,
  onHoldReason,
  submitting,
  uploadingCount,
  s3Keys = [],
}) => {
  if (submitting) {
    return null;
  }
  if (uploadingCount > 0) {
    return "Wait for document upload to finish";
  }
  if (!selectedRow) {
    return "No row selected";
  }
  if (status == null || status === "") {
    return "Select a status";
  }
  if (!expectedCompleteDate) {
    return "Select expected RCA completion date";
  }
  const expectedDay = moment(
    typeof expectedCompleteDate.format === "function"
      ? expectedCompleteDate.format("YYYY-MM-DD")
      : expectedCompleteDate,
    "YYYY-MM-DD",
  );
  if (expectedDay.isValid() && expectedDay.isBefore(moment().startOf("day"))) {
    return "Expected completion date cannot be in the past";
  }
  if (status === RCA_STATUS.ON_HOLD && !String(onHoldReason ?? "").trim()) {
    return "Enter On-hold status reason";
  }
  if (status === RCA_STATUS.COMPLETED) {
    const missing = [];
    if (!String(rootCauses ?? "").trim()) {
      missing.push("root cause(s)");
    }
    if (!String(conclusions ?? "").trim()) {
      missing.push("conclusions");
    }
    if (!assignedTo) {
      missing.push("assign to");
    }
    const attachmentCount = Array.isArray(s3Keys) ? s3Keys.length : 0;
    if (attachmentCount <= 0) {
      missing.push("attachment(s)");
    }
    if (missing.length > 0) {
      return `Complete required fields: ${missing.join(", ")}`;
    }
  }
  return null;
};

export const RCA_WORKFLOW_STATUS_OPTIONS = [
  { value: RCA_WORKFLOW_STATUS.NOT_STARTED, label: "Not Started" },
  { value: RCA_WORKFLOW_STATUS.IN_PROGRESS, label: "In Progress" },
  { value: RCA_WORKFLOW_STATUS.COMPLETED, label: "Completed" },
  { value: RCA_WORKFLOW_STATUS.ON_HOLD, label: "On-Hold" },
];

/**
 * @param {*} legacyStatus
 * @returns {string}
 */
export const normalizeLegacyRcaStatusToWorkflow = (legacyStatus) => {
  if (legacyStatus === RCA_STATUS.COMPLETED || legacyStatus === "completed") {
    return RCA_WORKFLOW_STATUS.COMPLETED;
  }
  if (legacyStatus === RCA_STATUS.ACTIVE || legacyStatus === "active") {
    return RCA_WORKFLOW_STATUS.IN_PROGRESS;
  }
  if (legacyStatus === RCA_WORKFLOW_STATUS.NOT_STARTED
    || legacyStatus === RCA_WORKFLOW_STATUS.IN_PROGRESS
    || legacyStatus === RCA_WORKFLOW_STATUS.ON_HOLD
    || legacyStatus === RCA_WORKFLOW_STATUS.COMPLETED) {
    return legacyStatus;
  }
  return RCA_WORKFLOW_STATUS.NOT_STARTED;
};

/**
 * Normalize optional workflow fields on RCA rows (no mock assignee injection).
 * @param {object} row
 * @returns {object}
 */
export const mapRcaStatusLabelFromApi = (statusLabel) => {
  const normalized = String(statusLabel ?? "").trim().toLowerCase();
  if (normalized === "active") {
    return "Active";
  }
  if (normalized === "completed") {
    return "Completed";
  }
  if (normalized === "in_progress" || normalized === "in progress") {
    return "In Progress";
  }
  if (normalized === "on_hold" || normalized === "on-hold" || normalized === "on hold") {
    return "On-Hold";
  }
  if (normalized === "not_started" || normalized === "not started") {
    return "Not Started";
  }
  return "";
};

export const enrichRcaRowWithMockFields = (row = {}) => {
  const nextRow = { ...row };
  if (!nextRow.assigned_to && nextRow.assignee_id != null) {
    nextRow.assigned_to = nextRow.assignee_id;
  }
  if (nextRow.rca_status_label) {
    const mapped = mapRcaStatusLabelFromApi(nextRow.rca_status_label);
    if (mapped === "Active") {
      nextRow.rca_status = RCA_STATUS.ACTIVE;
    } else if (mapped === "Completed") {
      nextRow.rca_status = RCA_STATUS.COMPLETED;
    }
  }
  if (!nextRow.rca_workflow_status && nextRow.rca_status != null) {
    nextRow.rca_workflow_status = normalizeLegacyRcaStatusToWorkflow(nextRow.rca_status);
  }
  if (nextRow.rca_status_label && !nextRow.rca_workflow_status) {
    const mapped = mapRcaStatusLabelFromApi(nextRow.rca_status_label);
    if (mapped === "In Progress") {
      nextRow.rca_workflow_status = RCA_WORKFLOW_STATUS.IN_PROGRESS;
    } else if (mapped === "On-Hold") {
      nextRow.rca_workflow_status = RCA_WORKFLOW_STATUS.ON_HOLD;
    } else if (mapped === "Not Started") {
      nextRow.rca_workflow_status = RCA_WORKFLOW_STATUS.NOT_STARTED;
    }
  }
  return nextRow;
};

/**
 * Resolve workflow status from API row (numeric legacy or string workflow).
 * @param {object} record
 * @returns {string}
 */
export const resolveRcaWorkflowStatus = (record = {}) => {
  if (record.rca_status_label) {
    const mapped = mapRcaStatusLabelFromApi(record.rca_status_label);
    if (mapped === "In Progress") {
      return RCA_WORKFLOW_STATUS.IN_PROGRESS;
    }
    if (mapped === "On-Hold") {
      return RCA_WORKFLOW_STATUS.ON_HOLD;
    }
    if (mapped === "Not Started") {
      return RCA_WORKFLOW_STATUS.NOT_STARTED;
    }
    if (mapped === "Completed") {
      return RCA_WORKFLOW_STATUS.COMPLETED;
    }
  }
  if (record.rca_workflow_status) {
    return normalizeLegacyRcaStatusToWorkflow(record.rca_workflow_status);
  }
  if (record.rca_status != null && record.rca_status !== "") {
    return normalizeLegacyRcaStatusToWorkflow(record.rca_status);
  }
  return RCA_WORKFLOW_STATUS.NOT_STARTED;
};

/**
 * Map an RCA row to API/filter status code (1–5).
 * @param {object} record
 * @returns {number}
 */
/**
 * RCA details modal Status field: only codes 1–5; otherwise unset (placeholder).
 */
export const resolveRcaDetailFormStatusValue = (record = null) => {
  if (!record) {
    return undefined;
  }
  const numericStatus = Number(record.rca_status);
  if (
    Number.isNaN(numericStatus)
    || numericStatus < RCA_STATUS.ACTIVE
    || numericStatus > RCA_STATUS.NOT_STARTED
  ) {
    return undefined;
  }
  return numericStatus;
};

export const resolveRcaFilterStatusCode = (record = {}) => {
  const numericStatus = Number(record.rca_status);
  if (numericStatus >= RCA_STATUS.ACTIVE && numericStatus <= RCA_STATUS.NOT_STARTED) {
    return numericStatus;
  }
  if (record.rca_status_label) {
    const mapped = mapRcaStatusLabelFromApi(record.rca_status_label);
    if (mapped === "Active") return RCA_STATUS.ACTIVE;
    if (mapped === "Completed") return RCA_STATUS.COMPLETED;
    if (mapped === "In Progress") return RCA_STATUS.IN_PROGRESS;
    if (mapped === "On-Hold") return RCA_STATUS.ON_HOLD;
    if (mapped === "Not Started") return RCA_STATUS.NOT_STARTED;
  }
  const workflow = resolveRcaWorkflowStatus(record);
  if (workflow === RCA_WORKFLOW_STATUS.IN_PROGRESS) return RCA_STATUS.IN_PROGRESS;
  if (workflow === RCA_WORKFLOW_STATUS.ON_HOLD) return RCA_STATUS.ON_HOLD;
  if (workflow === RCA_WORKFLOW_STATUS.NOT_STARTED) return RCA_STATUS.NOT_STARTED;
  if (workflow === RCA_WORKFLOW_STATUS.COMPLETED) return RCA_STATUS.COMPLETED;
  if (Number(record.rca_status) === RCA_STATUS.ACTIVE) return RCA_STATUS.ACTIVE;
  if (Number(record.rca_status) === RCA_STATUS.COMPLETED) return RCA_STATUS.COMPLETED;
  return RCA_STATUS.NOT_STARTED;
};

/**
 * @param {object} record
 * @param {string|number} assignToFilter — user id
 * @returns {boolean}
 */
export const matchesRcaAssignToFilter = (record = {}, assignToFilter = "") => {
  if (!assignToFilter) {
    return true;
  }
  const assigneeId = record.assigned_to ?? record.assignee_id;
  return String(assigneeId) === String(assignToFilter);
};

/**
 * @param {string} workflowStatus
 * @returns {string}
 */
export const getRcaWorkflowStatusLabel = (workflowStatus) => {
  const normalized = normalizeLegacyRcaStatusToWorkflow(workflowStatus);
  const matched = RCA_WORKFLOW_STATUS_OPTIONS.find((option) => option.value === normalized);
  return matched ? matched.label : EMPTY_CELL_VALUE;
};

/**
 * @param {string} workflowStatus
 * @returns {string}
 */
export const getRcaWorkflowStatusTagColor = (workflowStatus) => {
  const normalized = normalizeLegacyRcaStatusToWorkflow(workflowStatus);
  if (normalized === RCA_WORKFLOW_STATUS.COMPLETED) {
    return "success";
  }
  if (normalized === RCA_WORKFLOW_STATUS.IN_PROGRESS) {
    return "processing";
  }
  if (normalized === RCA_WORKFLOW_STATUS.ON_HOLD) {
    return "warning";
  }
  return "default";
};

/** Tag color for API status codes 1–5 (Data Ops RCA tables). */
const mapRcaNumericStatusTagColor = (statusCode) => {
  const numeric = Number(statusCode);
  if (Number.isNaN(numeric) || numeric < RCA_STATUS.ACTIVE || numeric > RCA_STATUS.NOT_STARTED) {
    return "default";
  }
  if (numeric === RCA_STATUS.COMPLETED) {
    return "success";
  }
  if (numeric === RCA_STATUS.ACTIVE || numeric === RCA_STATUS.IN_PROGRESS) {
    return "processing";
  }
  if (numeric === RCA_STATUS.ON_HOLD) {
    return "warning";
  }
  return "default";
};

/**
 * @param {string} assigneeKey
 * @returns {string}
 */
export const getRcaAssigneeLabel = (assigneeKey, assigneeName = "") => {
  if (assigneeName) {
    return assigneeName;
  }
  if (assigneeKey == null || assigneeKey === "") {
    return EMPTY_CELL_VALUE;
  }
  return String(assigneeKey);
};

/**
 * @param {object} record
 * @param {Record<string, string>} [userLabelById]
 * @returns {string}
 */
export const resolveRcaAssigneeDisplay = (record = {}, userLabelById = {}) => {
  if (record.assigned_to_name) {
    return record.assigned_to_name;
  }
  const assigneeId = record.assigned_to ?? record.assignee_id;
  if (assigneeId != null && userLabelById[assigneeId]) {
    return userLabelById[assigneeId];
  }
  return getRcaAssigneeLabel(assigneeId);
};

/** Aggregate options for RCA created/completed timeline — client-side rollup only. */
export const RCA_AGGREGATE_OPTIONS = [
  { label: "Daily", value: "day" },
  { label: "Weekly", value: "week" },
  { label: "Monthly", value: "month" },
];

export const DEFAULT_RCA_AGGREGATE = "day";

/** Fixed dual-line series keys for the RCA timeline chart. */
export const RCA_TIMELINE_SERIES = {
  CREATED: "created",
  COMPLETED: "completed",
};

const RCA_TIMELINE_LINE_DESCRIPTORS = [
  { dataKey: RCA_TIMELINE_SERIES.CREATED, legendName: "Created", seriesIndex: 0 },
  { dataKey: RCA_TIMELINE_SERIES.COMPLETED, legendName: "Completed", seriesIndex: 1 },
];

const EMPTY_STATUS_COUNTS = {
  activeCount: 0,
  completedCount: 0,
  notStartedCount: 0,
  inProgressCount: 0,
  onHoldCount: 0,
};

/**
 * Build rca_dashboard query object from RCA filter UI state.
 * @param {object} filters
 * @param {Function} formatDateRange — typically formatAntdDateRange
 * @returns {object|null} null when date range is invalid
 */
export const buildRcaDashboardParams = (filters, formatDateRange) => {
  const dateRange = formatDateRange(filters.dateRange);
  if (!dateRange) {
    return null;
  }

  const params = {
    date_range: dateRange,
    test_category_id: toOptionalIntegerIds(filters.testCategoryId),
    test_id: toOptionalIntegerIds(filters.testId),
    customer_pk: toOptionalIntegerId(filters.customerId),
    search: filters.search || "",
    created_by: toOptionalIntegerId(filters.createdBy),
    retest_reason: filters.retestReason || "",
  };

  if (filters.status !== "" && filters.status != null) {
    params.status = Number(filters.status);
  }

  if (filters.assignTo !== "" && filters.assignTo != null) {
    params.assigned_to = toOptionalIntegerId(filters.assignTo);
  }

  return params;
};

/**
 * Resolve display category name from a group or flat RCA row.
 * @param {object} row
 * @returns {string}
 */
const resolveCategoryName = (row = {}) => {
  const candidates = [
    row.test_category,
    row.category_name,
    row.test_category_name,
  ];
  const matched = candidates.find(
    (value) => value != null && String(value).trim() !== "",
  );
  return matched != null ? String(matched).trim() : "Unknown";
};

/**
 * @param {object} row
 * @returns {string|number|""}
 */
const resolveCategoryId = (row = {}) => (
  row.test_category_id != null && row.test_category_id !== ""
    ? row.test_category_id
    : ""
);

/**
 * Normalize a single RCA detail row with a stable rowKey.
 * @param {object} row
 * @param {number} index
 * @returns {object}
 */
const normalizeRcaListItem = (row = {}, index = 0) => ({
  ...row,
  rowKey: [
    row.rca_id,
    row.tis_pk,
    row.workorder_id,
    row.genista_id,
    row.test_id,
    index,
  ].filter((part) => part != null && part !== "").join("-") || `rca-item-${index}`,
});

/**
 * Whether a result item looks like a category group (has rca_list).
 * @param {object} item
 * @returns {boolean}
 */
const isGroupedRcaResultItem = (item) => (
  item != null && Array.isArray(item.rca_list)
);

/**
 * Count status columns from nested RCA rows when group counts are missing.
 * @param {object[]} rcaList
 * @returns {typeof EMPTY_STATUS_COUNTS}
 */
const countRcaListByStatus = (rcaList = []) => {
  const counts = { ...EMPTY_STATUS_COUNTS };

  rcaList.forEach((item) => {
    const status = Number(item.rca_status);
    if (status === RCA_STATUS.ACTIVE) {
      counts.activeCount += 1;
    } else if (status === RCA_STATUS.COMPLETED) {
      counts.completedCount += 1;
    } else if (status === RCA_STATUS.NOT_STARTED) {
      counts.notStartedCount += 1;
    } else if (status === RCA_STATUS.IN_PROGRESS) {
      counts.inProgressCount += 1;
    } else if (status === RCA_STATUS.ON_HOLD) {
      counts.onHoldCount += 1;
    }
  });

  return counts;
};

/**
 * Normalize a grouped API item into a table group row.
 * @param {object} group
 * @param {number} index
 * @returns {object}
 */
const normalizeGroupedRcaItem = (group = {}, index = 0) => {
  const rcaList = (Array.isArray(group.rca_list) ? group.rca_list : [])
    .map((item, itemIndex) => normalizeRcaListItem(item, itemIndex));

  const activeCount = Number(group.active_count) || 0;
  const completedCount = Number(group.completed_count) || 0;
  const notStartedCount = Number(group.not_started_count) || 0;
  const inProgressCount = Number(group.in_progress_count) || 0;
  const onHoldCount = Number(group.on_hold_count) || 0;

  const categoryId = resolveCategoryId(group);
  const categoryName = resolveCategoryName(group);

  return {
    ...group,
    categoryId,
    categoryName,
    activeCount,
    completedCount,
    notStartedCount,
    inProgressCount,
    onHoldCount,
    rcaList,
    rowKey: [
      categoryId,
      categoryName,
      index,
    ].filter((part) => part != null && part !== "").join("-") || `rca-group-${index}`,
  };
};

/**
 * Client-side group flat RCA rows by category (defensive if BE returns flat result).
 * @param {object[]} flatRows
 * @returns {object[]}
 */
const groupFlatRcaRowsByCategory = (flatRows = []) => {
  const groupsByKey = new Map();

  flatRows.forEach((row, index) => {
    const categoryId = resolveCategoryId(row);
    const categoryName = resolveCategoryName(row);
    const groupKey = categoryId !== ""
      ? `id:${categoryId}`
      : `name:${categoryName}`;

    if (!groupsByKey.has(groupKey)) {
      groupsByKey.set(groupKey, {
        test_category_id: categoryId === "" ? null : categoryId,
        test_category: categoryName,
        category_name: categoryName,
        rca_list: [],
      });
    }

    groupsByKey.get(groupKey).rca_list.push(normalizeRcaListItem(row, index));
  });

  return Array.from(groupsByKey.values()).map((group, index) => (
    normalizeGroupedRcaItem(group, index)
  ));
};

/**
 * Normalize rca_dashboard result into expandable category group rows.
 * Supports grouped payload (preferred) and flat payload (client-side group).
 * @param {object[]} [result]
 * @returns {object[]}
 */
export const normalizeRcaDashboardGroups = (result = []) => {
  const safeResult = Array.isArray(result) ? result : [];
  if (safeResult.length === 0) {
    return [];
  }

  if (safeResult.every(isGroupedRcaResultItem)) {
    return safeResult.map((group, index) => normalizeGroupedRcaItem(group, index));
  }

  // Mixed or flat: treat as flat RCA rows and group client-side.
  return groupFlatRcaRowsByCategory(safeResult);
};

/**
 * Normalize top-level count from rca_dashboard response.
 * @param {object} [count]
 * @returns {{ active: number, completed: number, total: number }}
 */
export const normalizeRcaDashboardCount = (count = {}) => {
  const active = Number(count?.active) || 0;
  const completed = Number(count?.completed) || 0;
  const notStarted = Number(count?.not_started ?? count?.not_started_count) || 0;
  const inProgress = Number(count?.in_progress ?? count?.in_progress_count) || 0;
  const onHold = Number(count?.on_hold ?? count?.on_hold_count) || 0;
  const total = Number(count?.total) || 0;

  return {
    active, completed, notStarted, inProgress, onHold, total,
  };
};

/**
 * @param {*} rcaStatus
 * @returns {string}
 */
export const getRcaStatusLabel = (rcaStatus, record = null) => {
  if (record) {
    if (record.rca_status_label) {
      const fromApi = mapRcaStatusLabelFromApi(record.rca_status_label);
      if (fromApi) {
        return fromApi;
      }
    }
    if (Number(record.rca_status) === RCA_STATUS.ACTIVE) {
      return "Active";
    }
    if (Number(record.rca_status) === RCA_STATUS.COMPLETED) {
      return "Completed";
    }
    if (Number(record.rca_status) === RCA_STATUS.IN_PROGRESS) {
      return "In Progress";
    }
    if (Number(record.rca_status) === RCA_STATUS.ON_HOLD) {
      return "On-Hold";
    }
    if (Number(record.rca_status) === RCA_STATUS.NOT_STARTED) {
      return "Not Started";
    }
    return getRcaWorkflowStatusLabel(resolveRcaWorkflowStatus(record));
  }
  if (rcaStatus === RCA_STATUS.ACTIVE) {
    return "Active";
  }
  if (rcaStatus === RCA_STATUS.COMPLETED) {
    return "Completed";
  }
  if (rcaStatus === RCA_STATUS.IN_PROGRESS) {
    return "In Progress";
  }
  if (rcaStatus === RCA_STATUS.ON_HOLD) {
    return "On-Hold";
  }
  if (rcaStatus === RCA_STATUS.NOT_STARTED) {
    return "Not Started";
  }
  if (rcaStatus == null || rcaStatus === "") {
    return EMPTY_CELL_VALUE;
  }
  return getRcaWorkflowStatusLabel(rcaStatus);
};

/**
 * Display/export status: only codes 1–5 get labels; otherwise "-".
 * Avoids workflow fallback showing "Not Started" for null/unknown.
 */
export const formatRcaStatusForDisplay = (value, record = null) => {
  if (value == null || value === "") {
    return "-";
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric) || numeric < 1 || numeric > 5) {
    return "-";
  }
  return getRcaStatusLabel(numeric, record);
};

/**
 * Ant Design Tag color for RCA status.
 * @param {*} rcaStatus
 * @returns {string}
 */
export const getRcaStatusTagColor = (rcaStatus, record = null) => {
  if (record != null) {
    const recordCode = Number(record.rca_status);
    if (!Number.isNaN(recordCode) && recordCode >= RCA_STATUS.ACTIVE && recordCode <= RCA_STATUS.NOT_STARTED) {
      return mapRcaNumericStatusTagColor(recordCode);
    }
  }
  const argCode = Number(rcaStatus);
  if (!Number.isNaN(argCode) && argCode >= RCA_STATUS.ACTIVE && argCode <= RCA_STATUS.NOT_STARTED) {
    return mapRcaNumericStatusTagColor(argCode);
  }
  if (record) {
    return getRcaWorkflowStatusTagColor(resolveRcaWorkflowStatus(record));
  }
  return getRcaWorkflowStatusTagColor(rcaStatus);
};

/**
 * Format API date for table display (date only).
 * @param {*} value
 * @returns {string}
 */
export const formatRcaDisplayDate = (value) => {
  if (value == null || value === "") {
    return EMPTY_CELL_VALUE;
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : String(value);
};

/**
 * Format API datetime for table display.
 * @param {*} value
 * @returns {string}
 */
export const formatRcaDisplayDateTime = (value) => {
  if (value == null || value === "") {
    return EMPTY_CELL_VALUE;
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY, h:mm a") : String(value);
};

/**
 * Count attached document keys on an RCA row.
 * @param {object} record
 * @returns {number}
 */
export const getRcaDocumentCount = (record = {}) => (
  Array.isArray(record.s3_keys) ? record.s3_keys.length : 0
);

/**
 * Flatten normalized category groups into one row per RCA (for charts + Excel).
 * Attaches the parent category display fields onto each nested item.
 * @param {object[]} [groups]
 * @returns {object[]}
 */
export const flattenRcaGroupsToRows = (groups = []) => {
  const safeGroups = Array.isArray(groups) ? groups : [];
  const flatRows = [];

  safeGroups.forEach((group) => {
    const rcaList = Array.isArray(group.rcaList) ? group.rcaList : [];
    rcaList.forEach((item) => {
      flatRows.push({
        ...item,
        categoryId: group.categoryId !== "" && group.categoryId != null
          ? group.categoryId
          : (item.test_category_id ?? ""),
        categoryName: group.categoryName || resolveCategoryName(item),
      });
    });
  });

  return flatRows;
};

const mapApiChartBarPoint = (item = {}, drillKind) => ({
  key: String(item.key ?? item.name ?? ""),
  name: String(item.name ?? "Unknown"),
  count: Number(item.count) || 0,
  percent: Number(item.percent) || 0,
  drillKind,
  customerId: item.customer_pk ?? "",
  testId: item.test_id ?? "",
  assigneeId: item.assigned_to ?? null,
});

/**
 * Bar chart series from rca_dashboard `chart_breakdown` (BE source of truth).
 * @param {object} [chartBreakdown]
 * @returns {{ customer: object[], test: object[], assignee: object[] }|null}
 */
export const normalizeRcaChartBarSeries = (chartBreakdown) => {
  if (!chartBreakdown || typeof chartBreakdown !== "object") {
    return null;
  }

  return {
    customer: (chartBreakdown.by_customer || []).map(
      (item) => mapApiChartBarPoint(item, "customer"),
    ),
    test: (chartBreakdown.by_test || []).map(
      (item) => mapApiChartBarPoint(item, "test"),
    ),
    assignee: (chartBreakdown.by_assignee || []).map(
      (item) => mapApiChartBarPoint(item, "assignee"),
    ),
  };
};

/**
 * Filter flattened RCA rows for a chart bar drill-down modal.
 * @param {object[]} groups
 * @param {object} point
 * @returns {object[]}
 */
export const filterRcaRowsForBarDrillDown = (groups = [], point = {}) => {
  const allRows = flattenRcaGroupsToRows(groups);
  if (!point?.drillKind) {
    return [];
  }

  if (point.drillKind === "customer") {
    return allRows.filter((row) => {
      const rowCustomerId = row.customer_pk ?? row.customer_id ?? "";
      return String(rowCustomerId) === String(point.customerId);
    });
  }

  if (point.drillKind === "test") {
    return allRows.filter(
      (row) => String(row.test_id) === String(point.testId),
    );
  }

  if (point.drillKind === "assignee") {
    if (point.key === "unassigned" || point.assigneeId == null) {
      return allRows.filter(
        (row) => row.assigned_to == null || row.assigned_to === "",
      );
    }
    return allRows.filter(
      (row) => String(row.assigned_to) === String(point.assigneeId),
    );
  }

  return [];
};

/**
 * Build a pseudo category group for RcaCategoryModal from a chart bar click.
 * @param {object} point
 * @param {object[]} groups
 * @returns {object|null}
 */
export const buildRcaModalGroupFromBarPoint = (point, groups = []) => {
  const rcaList = filterRcaRowsForBarDrillDown(groups, point);
  if (!rcaList.length) {
    return null;
  }

  const counted = countRcaListByStatus(rcaList);

  return {
    categoryName: point.name,
    ...counted,
    rcaList,
    rowKey: `bar-drill-${point.key}`,
  };
};

/**
 * Parse an RCA timestamp / date string into a moment day.
 * @param {*} value
 * @returns {moment.Moment|null}
 */
const parseRcaMoment = (value) => {
  if (value == null || String(value).trim() === "") {
    return null;
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
};

/**
 * Resolve period key / label / bounds for a row date + aggregate.
 * Same day/week/month contract as Retest client-side rollup.
 * @param {moment.Moment} rowDate
 * @param {string} aggregate — day | week | month
 * @returns {{
 *   periodKey: string,
 *   periodLabel: string,
 *   periodStart: string,
 *   periodEnd: string,
 * }|null}
 */
const resolveRcaPeriodBucket = (rowDate, aggregate) => {
  if (!rowDate || !rowDate.isValid()) {
    return null;
  }

  if (aggregate === "month") {
    const periodStart = rowDate.clone().startOf("month");
    const periodEnd = rowDate.clone().endOf("month");
    return {
      periodKey: periodStart.format("YYYY-MM"),
      periodLabel: periodStart.format("MMM YYYY"),
      periodStart: periodStart.format("YYYY-MM-DD"),
      periodEnd: periodEnd.format("YYYY-MM-DD"),
    };
  }

  if (aggregate === "week") {
    const periodStart = rowDate.clone().startOf("week");
    const periodEnd = rowDate.clone().endOf("week");
    return {
      periodKey: periodStart.format("YYYY-MM-DD"),
      periodLabel: `${periodStart.format("MM/DD/YYYY")} – ${periodEnd.format("MM/DD/YYYY")}`,
      periodStart: periodStart.format("YYYY-MM-DD"),
      periodEnd: periodEnd.format("YYYY-MM-DD"),
    };
  }

  const periodStart = rowDate.clone().startOf("day");
  return {
    periodKey: periodStart.format("YYYY-MM-DD"),
    periodLabel: periodStart.format("MM/DD/YYYY"),
    periodStart: periodStart.format("YYYY-MM-DD"),
    periodEnd: periodStart.format("YYYY-MM-DD"),
  };
};

/**
 * Ensure a period bucket exists and return it.
 * @param {Map<string, object>} periodByKey
 * @param {{ periodKey: string, periodLabel: string, periodStart: string, periodEnd: string }} period
 * @returns {object}
 */
const ensureRcaPeriodBucket = (periodByKey, period) => {
  if (!periodByKey.has(period.periodKey)) {
    periodByKey.set(period.periodKey, {
      periodKey: period.periodKey,
      periodLabel: period.periodLabel,
      periodStart: period.periodStart,
      periodEnd: period.periodEnd,
      [RCA_TIMELINE_SERIES.CREATED]: 0,
      [RCA_TIMELINE_SERIES.COMPLETED]: 0,
    });
  }
  return periodByKey.get(period.periodKey);
};

/**
 * Build dual-line created vs completed series from flattened RCA rows.
 * Created buckets use `created_at`; completed buckets use `actual_complete_date`.
 * Aggregation is FE-only (day | week | month) until BE adds an aggregate endpoint.
 *
 * @param {object[]} [groups] — normalized groups from normalizeRcaDashboardGroups
 * @param {string} [aggregate]
 * @returns {{
 *   chartRows: object[],
 *   lineDescriptors: Array<{ dataKey: string, legendName: string, seriesIndex: number }>,
 * }}
 */
export const buildRcaTimelineChartSeries = (
  groups = [],
  aggregate = DEFAULT_RCA_AGGREGATE,
) => {
  const flatRows = flattenRcaGroupsToRows(groups);
  const periodByKey = new Map();

  flatRows.forEach((row) => {
    const createdDate = parseRcaMoment(row.created_at);
    if (createdDate) {
      const period = resolveRcaPeriodBucket(createdDate, aggregate);
      if (period) {
        ensureRcaPeriodBucket(periodByKey, period)[RCA_TIMELINE_SERIES.CREATED] += 1;
      }
    }

    const completedDate = parseRcaMoment(row.actual_complete_date);
    if (completedDate) {
      const period = resolveRcaPeriodBucket(completedDate, aggregate);
      if (period) {
        ensureRcaPeriodBucket(periodByKey, period)[RCA_TIMELINE_SERIES.COMPLETED] += 1;
      }
    }
  });

  if (periodByKey.size === 0) {
    return {
      chartRows: [],
      lineDescriptors: [],
    };
  }

  const chartRows = Array.from(periodByKey.values())
    .sort((left, right) => left.periodKey.localeCompare(right.periodKey));

  return {
    chartRows,
    lineDescriptors: RCA_TIMELINE_LINE_DESCRIPTORS,
  };
};

/* -------------------------------------------------------------------------- */
/* Excel export                                                               */
/* -------------------------------------------------------------------------- */

const RCA_EXPORT_COLUMNS = [
  { key: "customer_company", header: "Customer" },
  { key: "customer_id", header: "Customer ID" },
  { key: "categoryName", header: "Test Category" },
  { key: "workorder_id", header: "WO" },
  { key: "genista_id", header: "GBID" },
  { key: "test_name", header: "Test" },
  { key: "rca_status", header: "Status" },
  { key: "assign_to", header: "Assign To" },
  { key: "age_days", header: "Age (days)" },
  { key: "created_by_name", header: "Created By" },
  { key: "created_at", header: "Created At" },
  { key: "expected_complete_date", header: "Expected Complete" },
  { key: "actual_complete_date", header: "Completed At" },
  { key: "documentCount", header: "Docs" },
  { key: "retest_reason", header: "Retest Reason" },
];

const buildRcaExportRow = (row) => ({
  customer_company: row.customer_company,
  customer_id: row.customer_id,
  categoryName: row.categoryName,
  workorder_id: row.workorder_id,
  genista_id: row.genista_id,
  test_name: row.test_name,
  rca_status: formatRcaStatusForDisplay(row.rca_status, row),
  assign_to: resolveRcaAssigneeDisplay(row) || "",
  age_days: row.age_days,
  created_by_name: row.created_by_name,
  created_at: formatRcaDisplayDateTime(row.created_at),
  expected_complete_date: formatRcaDisplayDate(row.expected_complete_date),
  actual_complete_date: formatRcaDisplayDate(row.actual_complete_date),
  documentCount: getRcaDocumentCount(row),
  retest_reason: row.retest_reason,
});

export const buildRcaExportRows = (groups) => (
  flattenRcaGroupsToRows(groups).map(buildRcaExportRow)
);

export const buildRcaExportFileName = (scopeLabel = "") => (
  buildDataOpsExportFileName("RCA", scopeLabel)
);

export const downloadRcaExportExcel = (rows, fileName) => {
  downloadDataOpsExcel({
    rows,
    columns: RCA_EXPORT_COLUMNS,
    fileName,
    sheetName: "RCA",
  });
};
